import { Box } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { QueryStatus } from 'react-query'
import { CellPlotDatum } from 'redux/slices/types'
import ActionPlotCellImage from '../ActionPlotCellImage'

interface ActionPlotCellImageWrapperProps
  extends Omit<Parameters<typeof ActionPlotCellImage>[0], 'cellId'> {
  cell: CellPlotDatum
  pinnedGroupId: number
}

const PlotCellImageWrapper = memo((props: ActionPlotCellImageWrapperProps) => {
  const {
    cell: { cellId },
  } = props
  return <ActionPlotCellImage {...props} cellId={cellId ?? ''} />
})

PlotCellImageWrapper.displayName = 'PlotCellImageWrapper'

interface ActionPlotCellImagesProps {
  pinnedGroupId?: number
  cells?: CellPlotDatum[]
  imgProps?: Parameters<typeof ActionPlotCellImage>[0]['imgProps']
  handleCellStatusUpdate?(cell: CellPlotDatum, status?: QueryStatus): void
}

export const ActionPlotCellImages = memo(({
  pinnedGroupId = -1,
  cells,
  imgProps,
  handleCellStatusUpdate,
}: ActionPlotCellImagesProps): JSX.Element => {
  const handleCellStatusChange = useCallback((cell: CellPlotDatum) => (status?: QueryStatus) => {
    if (handleCellStatusUpdate) handleCellStatusUpdate(cell, status)
  }, [handleCellStatusUpdate])

  // Virtualize rendering for large cell lists to prevent memory issues
  const maxCellsToRender = 50 // Reduce from 100 to 50 for better performance
  const cellsToRender = useMemo(() => 
    cells?.slice(0, maxCellsToRender) || [], 
    [cells, maxCellsToRender]
  )

  return (
    <Box sx={{ textAlign: 'center' }}>
      {cellsToRender.map((cell: CellPlotDatum, i: number) => {
        const key = `${cell.cellId}_${i}`
        return (
          <PlotCellImageWrapper
            pinnedGroupId={pinnedGroupId}
            key={key}
            cell={cell}
            handleStatusChange={handleCellStatusChange(cell)}
            imgProps={{ ...imgProps, style: { borderRadius: '10px', ...imgProps?.style } }}
          />
        )
      })}
    </Box>
  )
})

ActionPlotCellImages.displayName = 'ActionPlotCellImages'

export default ActionPlotCellImages
